/**
 * Módulo Pedidos — redesenhado com o Lucas em 03/08, inspirado no Mercado
 * Livre. Não é porte do sistema atual: ele decidiu redesenhar antes de portar.
 *
 * A ANATOMIA, e por que ela é assim:
 *
 *   TOPO — busca sempre visível, a obra escolhida (o "endereço de entrega":
 *   muda o que 'adicionar' significa) e o carrinho com contagem. É o que o
 *   Mercado Livre acerta: o caminho para comprar nunca sai da tela.
 *
 *   BARRA DE GRUPOS — as categorias com item ativo, uma linha, rolável no
 *   celular. Um toque leva à vitrine já filtrada.
 *
 *   PÁGINA INICIAL ≠ VITRINE — pedido do Lucas. A inicial é um painel: o
 *   quadro "saia logo" (lotes vencendo, para estimular a saída antes de
 *   virarem perda) e os atalhos. A vitrine é outra tela, a de busca.
 *
 *   IMPORTAR LISTA — recebe texto colado, foto ou PDF e confere contra o
 *   estoque. A tela mais nova do sistema; ver `lista.ts`.
 */

import { api } from '../../api.js';
import { escapar, numero } from '../../comum/formato.js';
import {
  escolherObra,
  aoMudarCarrinho,
  carregarCarrinho,
  entrarEmConsulta,
  emConsulta,
  consumirPendente,
  temPendente,
  adicionar,
  type ObraEscolhida,
} from './estado.js';
import { montarVitrine, type NoArvore } from './vitrine.js';
import { montarMenuLateral, type ItemMenu } from '../../comum/menu-lateral.js';
import { montarCarrinho } from './carrinho.js';
import { montarLista } from './lista.js';
import { montarMeus } from './meus.js';

interface Inicio {
  vencendo: {
    codigo: string;
    nome: string;
    unidade: string | null;
    saldo: number;
    validade: string;
    marca: string | null;
    /** Dias até vencer; negativo = vencido há tantos dias. */
    dias: number;
    vencido: boolean;
    foto: string | null;
    miniatura: string | null;
  }[];
  categorias: NoArvore[];
}

type Tela =
  | { qual: 'inicio' }
  | { qual: 'vitrine'; categoria?: number; grupo?: number; subgrupo?: number; busca?: string }
  | { qual: 'carrinho' }
  | { qual: 'lista' }
  | { qual: 'meus' };

export function montarPedidos(raiz: HTMLElement): void {
  let inicio: Inicio | undefined;

  raiz.innerHTML = `
    <div class="ped">
      <div class="ped-topo">
        <input class="ped-busca" id="ped-busca" type="search"
               placeholder="Buscar material…" autocomplete="off" spellcheck="false">
        <button class="ped-obra" id="ped-obra" title="Trocar a obra">🏗️ <span id="ped-obra-nome">…</span></button>
        <button class="ped-cart" id="ped-cart" title="Carrinho">🛒<span class="ped-cart-n" id="ped-cart-n" hidden></span></button>
      </div>
      <div class="modulo-com-menu">
        <aside id="menu-ped"></aside>
        <div id="ped-tela"><p class="carregando">Carregando…</p></div>
      </div>
    </div>`;

  const tela = raiz.querySelector<HTMLElement>('#ped-tela')!;

  /**
   * O MENU LATERAL, sempre presente — inclusive na página inicial. Pedido do
   * Lucas: a cascata de categorias visível o tempo todo, "para criar uma
   * estabilidade visual dentro do módulo". Antes a árvore só existia dentro da
   * vitrine, e a inicial tinha outra navegação (a barra horizontal): a cada
   * troca de tela o jeito de se mover mudava.
   */
  const itensDoMenu = (): ItemMenu[] => {
    const cascata = (nos: NoArvore[], prefixo: string): ItemMenu[] =>
      nos.map((n) => ({
        chave: `${prefixo}${n.id}`,
        nome: n.nome,
        badge: n.itens,
        filhos: n.filhos.length
          ? cascata(n.filhos, prefixo === 'cat:' ? `cat:${n.id}|gru:` : `${prefixo}${n.id}|sub:`)
          : [],
      }));
    return [
      { chave: 'inicio', nome: 'Início', emoji: '🏠' },
      { chave: 'lista', nome: 'Importar lista', emoji: '📋' },
      { chave: 'carrinho', nome: 'Carrinho', emoji: '🛒' },
      { chave: 'meus', nome: 'Meus pedidos', emoji: '🚚' },
      ...(inicio?.categorias.length
        ? [{ chave: 'materiais', nome: 'Materiais', emoji: '🧱', separaAntes: true,
             filhos: cascata(inicio.categorias, 'cat:') }]
        : []),
    ];
  };

  const menu = montarMenuLateral(raiz.querySelector<HTMLElement>('#menu-ped')!, itensDoMenu(), (chave) => {
    busca.value = '';
    if (chave === 'inicio' || chave === 'materiais') return abrir({ qual: 'inicio' });
    if (chave === 'lista') return abrir({ qual: 'lista' });
    if (chave === 'carrinho') return abrir({ qual: 'carrinho' });
    if (chave === 'meus') return abrir({ qual: 'meus' });
    // cat:1 | cat:1|gru:2 | cat:1|gru:2|sub:3
    const partes = Object.fromEntries(chave.split('|').map((p) => p.split(':') as [string, string]));
    abrir({
      qual: 'vitrine',
      ...(partes['cat'] ? { categoria: Number(partes['cat']) } : {}),
      ...(partes['gru'] ? { grupo: Number(partes['gru']) } : {}),
      ...(partes['sub'] ? { subgrupo: Number(partes['sub']) } : {}),
    });
  });
  const busca = raiz.querySelector<HTMLInputElement>('#ped-busca')!;
  const cartN = raiz.querySelector<HTMLElement>('#ped-cart-n')!;
  const obraNome = raiz.querySelector<HTMLElement>('#ped-obra-nome')!;

  // O contador do carrinho acompanha qualquer mudança, venha de onde vier.
  aoMudarCarrinho((c) => {
    const n = c.itens.length;
    cartN.hidden = n === 0;
    cartN.textContent = String(n);
  });

  // ------------------------------------------------------------- navegação

  function abrir(t: Tela): void {
    if (t.qual !== 'vitrine') menu.marcar(t.qual === 'inicio' ? 'inicio' : t.qual);
    if (t.qual === 'inicio') return desenharInicio();
    if (t.qual === 'vitrine') {
      return montarVitrine(tela, {
        ...(t.categoria !== undefined ? { categoria: t.categoria } : {}),
        ...(t.grupo !== undefined ? { grupo: t.grupo } : {}),
        ...(t.subgrupo !== undefined ? { subgrupo: t.subgrupo } : {}),
        ...(t.busca !== undefined ? { busca: t.busca } : {}),
        arvore: inicio?.categorias ?? [],
        // Em consulta, "adicionar" não adiciona: guarda o item e traz a
        // escolha de obra. Ver `trocarObra`, que completa o caminho.
        aoPedirObra: () => void trocarObra(),
      });
    }
    if (t.qual === 'meus') return montarMeus(tela);
    if (t.qual === 'carrinho') {
      return montarCarrinho(
        tela,
        () => abrir({ qual: 'inicio' }),
        // Depois do envio, o caminho natural é acompanhar — o pedido recém
        // enviado aparece no topo de "Meus pedidos".
        () => abrir({ qual: 'meus' }),
      );
    }
    montarLista(tela, () => abrir({ qual: 'carrinho' }));
  }

  raiz.querySelector('#ped-cart')!.addEventListener('click', () => abrir({ qual: 'carrinho' }));
  raiz.querySelector('#ped-obra')!.addEventListener('click', () => void trocarObra());

  let temporizador: number | undefined;
  busca.addEventListener('input', () => {
    window.clearTimeout(temporizador);
    temporizador = window.setTimeout(() => {
      const termo = busca.value.trim();
      if (termo) abrir({ qual: 'vitrine', busca: termo });
    }, 350);
  });

  // ---------------------------------------------------------- página inicial

  function desenharInicio(): void {
    if (!inicio) return;
    const v = inicio.vencendo;

    tela.innerHTML = `
      <section class="ped-painel">
        <h3>Prioridade de saída — validade</h3>
        ${
          v.length === 0
            ? `<p class="dica">Nenhum lote vencendo em até 60 dias, nem vencido há menos de 60.</p>`
            : `<p class="dica">Lotes com saldo que vencem em até 60 dias, ou que venceram há menos de 60. Priorizar no atendimento.</p>
               <div class="ped-vencendo">
                 ${v
                   .map(
                     (i) => `
                   <button class="ped-vcard ${i.vencido ? 'vencido' : ''}" data-add="${escapar(i.codigo)}">
                     <span class="ped-vfoto">${
                       i.miniatura
                         ? `<img src="${i.miniatura}" alt="">`
                         : i.foto
                           ? `<img src="${escapar(api.foto(i.foto) ?? '')}" alt="" loading="lazy">`
                           : '<span class="sem">sem foto</span>'
                     }</span>
                     <span class="ped-vvalidade">${prazoDaValidade(i.dias)} <span class="ped-vdata">· ${dataBr(i.validade)}</span></span>
                     <b>${escapar(i.nome)}</b>
                     <span class="sub-inline">${numero(i.saldo)} ${escapar(i.unidade ?? '')}${
                       i.marca ? ` · ${escapar(i.marca)}` : ''
                     }</span>
                     <span class="ped-vadd">Ver item</span>
                   </button>`,
                   )
                   .join('')}
               </div>`
        }
      </section>

      <section class="ped-painel">
        <h3>Atalhos</h3>
        <div class="ped-atalhos">
          <button class="ped-atalho" data-ir2="lista">
            <b>Importar lista</b>
            <small>Texto, foto ou PDF. O sistema localiza cada linha no estoque.</small>
          </button>
          <button class="ped-atalho" data-ir2="carrinho">
            <b>Carrinho</b>
            <small>Conferir e enviar o pedido da obra selecionada.</small>
          </button>
        </div>
      </section>`;

    tela.querySelectorAll<HTMLElement>('[data-ir2]').forEach((b) => {
      b.addEventListener('click', () =>
        abrir(b.dataset['ir2'] === 'lista' ? { qual: 'lista' } : { qual: 'carrinho' }),
      );
    });
    tela.querySelectorAll<HTMLElement>('[data-add]').forEach((b) => {
      b.addEventListener('click', () => abrir({ qual: 'vitrine', busca: b.dataset['add']! }));
    });
  }

  // ------------------------------------------------------------------- obra

  /**
   * A escolha de obra — e o ponto de retorno do modo consulta.
   *
   * Quando chega aqui com um item pendente (a pessoa estava só consultando e
   * mandou adicionar), a tela DIZ isso e, escolhida a obra, o item entra no
   * carrinho dela antes de qualquer outra coisa. Foi o pedido do Lucas:
   * "leve o usuário de volta à janela de escolha de obra e assim coloque o
   * produto dentro do carrinho, após isso tratar como o carrinho da obra".
   */
  async function trocarObra(): Promise<void> {
    // As duas juntas: as obras e quantos itens JÁ existem no carrinho de cada
    // uma. Pedido do Lucas — sem isso, um carrinho montado ontem e esquecido
    // fica invisível, e a pessoa monta outro do zero na mesma obra.
    const [r, c] = await Promise.all([
      api.get<{ obras: { codigo: string; nome: string }[] }>('/api/obras'),
      api
        .get<{ carrinhos: { obra: string; obraNome: string; itens: number }[] }>('/api/carrinhos')
        .catch(() => ({ carrinhos: [] })),
    ]);
    const noCarrinho = new Map(c.carrinhos.map((x) => [x.obra, x.itens]));
    const esperando = temPendente();

    tela.innerHTML = `
      <section class="ped-painel">
        <h3>${esperando ? 'Para qual obra vai este material?' : 'Selecione a obra'}</h3>
        ${
          esperando
            ? `<p class="aviso">Você estava em consulta. Escolha a obra e o item entra no carrinho dela.</p>`
            : `<p class="dica">Cada obra tem o seu carrinho. O que for adicionado vai para o carrinho da obra selecionada.</p>`
        }
        <div class="ped-obras">
          ${r.obras
            // Código do maior para o menor (11/08, Lucas) — a obra mais nova
            // tem o código mais alto e é a que mais recebe pedido.
            .slice()
            .sort((a, b) => b.codigo.localeCompare(a.codigo, 'pt-BR', { numeric: true }))
            .map((o) => {
              const n = noCarrinho.get(o.codigo) ?? 0;
              return `<button class="ped-obra-opcao ${n ? 'com-carrinho' : ''}"
                        data-obra="${escapar(o.codigo)}" data-nome="${escapar(o.nome)}">
                  <b>${escapar(o.nome)}</b>
                  <span class="sub-inline">${escapar(o.codigo)}</span>
                  ${n ? `<span class="ped-obra-cart">🛒 ${n} item(ns) no carrinho</span>` : ''}
                </button>`;
            })
            .join('')}
        </div>
        ${
          esperando
            ? ''
            : `<div class="conf-acoes">
                 <button class="btn btn-claro" id="so-consulta">Apenas consultar o catálogo</button>
               </div>
               <p class="dica">Em consulta não há carrinho. Se decidir pedir algo, a obra é solicitada na hora.</p>`
        }
      </section>`;

    tela.querySelector('#so-consulta')?.addEventListener('click', () => {
      entrarEmConsulta();
      obraNome.textContent = 'consulta';
      abrir({ qual: 'inicio' });
    });

    tela.querySelectorAll<HTMLElement>('[data-obra]').forEach((b) => {
      b.addEventListener('click', async () => {
        const obra: ObraEscolhida = { codigo: b.dataset['obra']!, nome: b.dataset['nome']! };
        escolherObra(obra);
        obraNome.textContent = obra.nome;
        const p = consumirPendente();
        try {
          await carregarCarrinho(obra.codigo);
          if (p) {
            await adicionar(obra.codigo, p.codigo, p.quantidade);
            abrir({ qual: 'carrinho' });
            return;
          }
        } catch (e) {
          /**
           * Falhar aqui SÓ pode ser silencioso quando não havia item pendente
           * — aí é só o carrinho que não abriu, e a vitrine continua servindo.
           *
           * Com item pendente é diferente: a tela acabou de prometer que ele
           * entraria ao escolher a obra. Engolir o erro deixaria a pessoa
           * procurando no carrinho um item que nunca chegou lá.
           */
          if (p) {
            tela.innerHTML = `
              <section class="ped-painel">
                <p class="aviso">Escolhi a obra <b>${escapar(obra.nome)}</b>, mas não consegui pôr
                  o item no carrinho. ${escapar(e instanceof Error ? e.message : '')}</p>
                <button class="btn" id="ver-vitrine">Voltar ao catálogo</button>
              </section>`;
            tela.querySelector('#ver-vitrine')!.addEventListener('click', () =>
              abrir({ qual: 'inicio' }),
            );
            return;
          }
        }
        abrir({ qual: 'inicio' });
      });
    });
  }


  // -------------------------------------------------------------- primeira vez

  void (async () => {
    try {
      inicio = await api.get<Inicio>('/api/pedidos/inicio');
      menu.reabastecer(itensDoMenu());
    } catch {
      tela.innerHTML = `<p class="aviso">Não consegui carregar. Recarregue a página.</p>`;
      return;
    }
    // SEMPRE começa pela escolha da obra. Não há caminho que pule esta tela —
    // a obra deixou de ser lembrada entre visitas por decisão do Lucas, e é
    // aqui que "apenas consultar" também é oferecido.
    obraNome.textContent = 'escolher obra';
    void trocarObra();
  })();
}

function dataBr(iso: string): string {
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

/** "Vence em 12 dias", "VENCE HOJE", "VENCIDO HÁ 3 DIAS" — a contagem que o Lucas pediu (29/10). */
function prazoDaValidade(dias: number): string {
  const n = Math.abs(dias);
  const plural = n === 1 ? 'dia' : 'dias';
  if (dias < 0) return `VENCIDO HÁ ${n} ${plural.toUpperCase()}`;
  if (dias === 0) return 'VENCE HOJE';
  return `Vence em ${n} ${plural}`;
}
